<template>
  <div class="shift-view">
    <div class="container my-4">
      <!-- Header with actions -->
      <div class="header-section">
        <div class="d-flex align-items-center">
          <h1>Shift Details</h1>
          <span 
            class="badge text-bg-info ms-3"
            v-if="shift.is_overnight"
          >
            Overnight Shift
          </span>
        </div>
        <div class="action-buttons">
          <button
            v-if="$canAccess('ShiftEdit')"
            class="btn btn-primary"
            @click="editShift()"
          >
            <i class="fas fa-edit"></i> Edit
          </button>
          <button
            class="btn btn-secondary ms-2"
            @click="$router.push({ name: 'ShiftList' })"
          >
            <i class="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Shift Details -->
      <div v-if="!loading && shift" class="shift-details">
        <div class="row">
          <div class="col-md-6">
            <div class="card mb-4">
              <div class="card-header">
                <h5>Basic Information</h5>
              </div>
              <div class="card-body">
                <div class="detail-item">
                  <div class="detail-label">Name:</div>
                  <div class="detail-value">{{ shift.name }}</div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">Department:</div>
                  <div class="detail-value">{{ shift.department?.name }}</div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">Start Time:</div>
                  <div class="detail-value">{{ $convertTo12Hour(shift.start_time) }}</div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">End Time:</div>
                  <div class="detail-value">{{ $convertTo12Hour(shift.end_time) }}</div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">Duration:</div>
                  <div class="detail-value">{{ formatDuration(shift.duration_minutes) }}</div>
                </div>
                
                <div class="detail-item">
                  <div class="detail-label">Overnight:</div>
                  <div class="detail-value">
                    <span :class="shift.is_overnight ? 'badge bg-info' : 'badge bg-secondary'">
                      {{ shift.is_overnight ? 'Yes' : 'No' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="card mb-4" v-if="shift.description">
              <div class="card-header">
                <h5>Description</h5>
              </div>
              <div class="card-body">
                <p class="description-text">{{ shift.description }}</p>
              </div>
            </div>
          </div>
          
          <div class="col-md-6">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5>Staffing Requirements</h5>
                <button 
                  v-if="$canAccess('StaffingRequirementManage')"
                  class="btn btn-sm btn-outline-primary"
                  @click="manageStaffingRequirements()"
                >
                  <i class="fas fa-users-cog"></i> Manage Requirements
                </button>
              </div>
              
              <div class="card-body">
                <div v-if="loading_requirements" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                
                <div v-else-if="staffingRequirements.length === 0" class="text-center py-3">
                  <p class="text-muted">No staffing requirements have been defined for this shift.</p>
                  <button 
                    v-if="$canAccess('StaffingRequirementAdd')"
                    class="btn btn-sm btn-outline-primary"
                    @click="addStaffingRequirement()"
                  >
                    <i class="fas fa-plus-circle"></i> Add Staffing Requirement
                  </button>
                </div>
                
                <div v-else>
                  <div class="table-responsive">
                    <table class="table table-sm">
                      <thead>
                        <tr>
                          <th>Role</th>
                          <th>Required Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="req in staffingRequirements" :key="req.id">
                          <td>
                            <span v-if="req.role === 'regular'">Regular Nurses</span>
                            <span v-else-if="req.role === 'charge_nurse'">Charge Nurses</span>
                            <span v-else-if="req.role === 'team_lead'">Team Leads</span>
                            <span v-else>{{ req.role }}</span>
                          </td>
                          <td>{{ req.count_required }}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr class="table-secondary">
                          <th>Total Staff Required:</th>
                          <th>{{ calculateTotalStaff() }}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="card mt-4">
              <div class="card-header">
                <h5>Recent Assignments</h5>
              </div>
              <div class="card-body">
                <div v-if="loading_assignments" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                
                <div v-else-if="recentAssignments.length === 0" class="text-center py-3">
                  <p class="text-muted">No recent assignments for this shift.</p>
                </div>
                
                <div v-else>
                  <ul class="list-group list-group-flush">
                    <li class="list-group-item" v-for="assignment in recentAssignments" :key="assignment.id">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{{ assignment.nurse.name }}</strong>
                          <span class="badge bg-secondary ms-2">{{ formatRole(assignment.role) }}</span>
                          <div class="small text-muted">{{ formatDate(assignment.assignment_date) }}</div>
                        </div>
                        <button 
                          class="btn btn-sm btn-outline-info"
                          @click="viewAssignment(assignment.id)"
                        >
                          <i class="fas fa-eye"></i>
                        </button>
                      </div>
                    </li>
                  </ul>
                  
                  <div class="text-center mt-3" v-if="recentAssignments.length >= 5">
                    <button 
                      class="btn btn-sm btn-outline-secondary"
                      @click="viewAllAssignments()"
                    >
                      View All Assignments
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShiftView',
  data() {
    return {
      shift: {},
      staffingRequirements: [],
      recentAssignments: [],
      loading: false,
      loading_requirements: false,
      loading_assignments: false,
      error: null
    }
  },
  methods: {
    async fetchShift() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get(`/shifts/${this.$route.params.id}`);
        this.shift = response.data.data;
      } catch (error) {
        console.error('Error fetching shift:', error);
        this.error = 'Could not load shift details. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    
    async fetchStaffingRequirements() {
      if (!this.shift.id) return;
      
      this.loading_requirements = true;
      
      try {
        const response = await this.$api.get(`/staffing-requirements/shift/${this.shift.id}`);
        this.staffingRequirements = response.data.data;
      } catch (error) {
        console.error('Error fetching staffing_requirements requirements:', error);
      } finally {
        this.loading_requirements = false;
      }
    },
    
    async fetchRecentAssignments() {
      if (!this.shift.id) return;
      
      this.loading_assignments = true;
      
      try {
        // Get the last 5 assignments for this shift
        const response = await this.$api.get(`/shift-assignments/shift/${this.shift.id}?limit=5`);
        this.recentAssignments = response.data.data;
      } catch (error) {
        console.error('Error fetching recent assignments:', error);
      } finally {
        this.loading_assignments = false;
      }
    },
    
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return 'N/A';
      
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      
      let result = '';
      if (hours === 1) result += '1 hour';
      else if (hours > 1) result += `${hours} hours`;
      
      if (mins > 0) {
        if (result) result += ' and ';
        result += `${mins} minute${mins !== 1 ? 's' : ''}`;
      }
      
      return result;
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      
      const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    
    formatRole(role) {
      switch (role) {
        case 'regular': return 'Regular';
        case 'charge_nurse': return 'Charge Nurse';
        case 'team_lead': return 'Team Lead';
        default: return role.charAt(0).toUpperCase() + role.slice(1);
      }
    },
    
    calculateTotalStaff() {
      return this.staffingRequirements.reduce((total, req) => total + req.count_required, 0);
    },
    
    editShift() {
      this.$router.push({ 
        name: 'ShiftEdit', 
        params: { id: this.shift.id } 
      });
    },
    
    manageStaffingRequirements() {
      this.$router.push({ 
        name: 'StaffingRequirementManage', 
        params: { shiftId: this.shift.id } 
      });
    },
    
    addStaffingRequirement() {
      this.$router.push({ 
        name: 'StaffingRequirementAdd', 
        params: { shiftId: this.shift.id } 
      });
    },
    
    viewAssignment(assignmentId) {
      this.$router.push({ 
        name: 'ShiftAssignmentView', 
        params: { id: assignmentId } 
      });
    },
    
    viewAllAssignments() {
      this.$router.push({ 
        name: 'ShiftAssignmentList', 
        query: { shift_id: this.shift.id } 
      });
    }
  },
  mounted() {
    this.fetchShift();
  },
  watch: {
    'shift.id': function() {
      // When shift data is loaded, fetch related data
      if (this.shift.id) {
        this.fetchStaffingRequirements();
        this.fetchRecentAssignments();
      }
    },
    '$route.params.id': function() {
      // Refetch data if route parameter changes
      this.fetchShift();
    }
  }
}
</script>

<style scoped>
.shift-view {
  background-color: #f8f9fa;
  min-height: 80vh;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.detail-item {
  display: flex;
  margin-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
}

.detail-label {
  width: 120px;
  font-weight: 600;
  color: #495057;
}

.detail-value {
  flex: 1;
}

.description-text {
  white-space: pre-line;
  color: #6c757d;
}

.card {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border: none;
  margin-bottom: 20px;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 12px 15px;
}

.card-header h5 {
  margin-bottom: 0;
  font-size: 1.1rem;
  color: #343a40;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action-buttons {
    margin-top: 15px;
    width: 100%;
    display: flex;
    gap: 10px;
  }
  
  .action-buttons .btn {
    flex: 1;
  }
  
  .detail-item {
    flex-direction: column;
  }
  
  .detail-label {
    width: 100%;
    margin-bottom: 5px;
  }
}
</style>
